import * as React from "react"

import type { SortDescriptor } from "./types"

const SORT_STORAGE_PREFIX = "coval-table-sort:"

interface StoredSort {
  sort: SortDescriptor | null
}

/**
 * The storage key the sort persists under: the page path, which carries the
 * organization and workspace segments, so each page in each workspace keeps its
 * own sort. The explicit key, else the table's `aria-label` or `aria-labelledby`
 * id, is appended so two tables on one page keep separate sorts. `null` opts
 * out.
 */
function resolveSortStorageKey(
  storageKey: string | null | undefined,
  label: string | undefined,
) {
  if (storageKey === null) {
    return null
  }
  const pageKey = `${SORT_STORAGE_PREFIX}${window.location.pathname}`
  const suffix = storageKey ?? label
  return suffix ? `${pageKey}:${suffix}` : pageKey
}

function serializeSort(sort: SortDescriptor | null) {
  return sort ? `${sort.columnId}:${sort.direction}` : ""
}

/**
 * The stored sort, or `undefined` when nothing usable is stored. Any other shape
 * reads as "no preference" rather than throwing.
 */
function readStoredSort(key: string): StoredSort | undefined {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) {
      return undefined
    }
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null) {
      return { sort: null }
    }
    if (typeof parsed !== "object") {
      return undefined
    }
    const { columnId, direction } = parsed as Record<string, unknown>
    if (
      typeof columnId === "string" &&
      (direction === "asc" || direction === "desc")
    ) {
      return { sort: { columnId, direction } }
    }
    return undefined
  } catch {
    return undefined
  }
}

function writeStoredSort(key: string, sort: SortDescriptor | null) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(sort))
  } catch {
    // Quota exceeded / storage blocked — the sort stays in memory only.
  }
}

interface UseSortPersistenceParams {
  /** The active sort, or `null` when unsorted. */
  sort: SortDescriptor | null
  storageKey: string | null | undefined
  /**
   * The table's `aria-label`, else its `aria-labelledby` id, separating tables
   * on one page by default.
   */
  label: string | undefined
  /** The sort a page with nothing stored starts from. */
  defaultSort: SortDescriptor | null
  /** Whether the stored sort can be applied to the current columns. */
  canRestore: (sort: SortDescriptor | null) => boolean
  restore: (sort: SortDescriptor | null) => void
}

/**
 * Persists the active sort to `sessionStorage` for the tab's session and
 * restores it on mount, so a refresh keeps the user's sort. Storage is read in
 * a layout effect (never during render) so the server render and first client
 * render agree, and the restored sort lands before paint.
 *
 * A stored sort whose column isn't sortable yet (columns that load
 * asynchronously) waits until it is, unless the user sorts from a header
 * first; a consumer-driven `defaultSort` change doesn't discard it. The key is
 * re-resolved on every commit, so an in-place organization or workspace switch
 * restores that page's own sort, or its default sort when none is stored.
 */
export function useSortPersistence({
  sort,
  storageKey,
  label,
  defaultSort,
  canRestore,
  restore,
}: UseSortPersistenceParams) {
  const keyRef = React.useRef<string | null | undefined>(undefined)
  // The serialized sort storage already holds for `keyRef`; a change from it
  // is a new sort to write.
  const lastWrittenRef = React.useRef("")
  // A stored sort not yet applied: waiting on its column, or on the render
  // that applies it.
  const pendingRef = React.useRef<StoredSort | null>(null)
  // Set by a header sort since the last commit; only that discards a pending
  // stored sort.
  const userSortedRef = React.useRef(false)
  // The sort a restore was requested from. Strict Mode re-runs this effect
  // before the restore renders; skipping while the sort is unchanged keeps the
  // restore (and its `onSortChange`) to one call.
  const restoreRequestedFromRef = React.useRef<string | null>(null)

  React.useLayoutEffect(() => {
    const key = resolveSortStorageKey(storageKey, label)
    const current = serializeSort(sort)

    if (key !== keyRef.current) {
      // On mount the sort already is the default; after an in-place key change
      // it is the previous page's, so fall back to the default.
      const fallback =
        keyRef.current === undefined ? null : { sort: defaultSort }
      keyRef.current = key
      lastWrittenRef.current = current
      pendingRef.current = key ? (readStoredSort(key) ?? fallback) : null
      restoreRequestedFromRef.current = null
    }
    if (!key) {
      return
    }

    const userSorted = userSortedRef.current
    userSortedRef.current = false

    const pending = pendingRef.current
    if (pending) {
      if (userSorted) {
        // The user sorted before the stored sort could apply; theirs wins.
        pendingRef.current = null
      } else {
        if (serializeSort(pending.sort) === current) {
          pendingRef.current = null
          lastWrittenRef.current = current
        } else if (
          restoreRequestedFromRef.current !== current &&
          canRestore(pending.sort)
        ) {
          restoreRequestedFromRef.current = current
          restore(pending.sort)
        }
        return
      }
    }

    if (current !== lastWrittenRef.current) {
      lastWrittenRef.current = current
      writeStoredSort(key, sort)
    }
  })

  return React.useCallback(() => {
    userSortedRef.current = true
  }, [])
}
